/**
 * Zeilendialog der Betriebsmodi (ELiA-Formulare 16.1.10 und 4.5)
 *
 * Zwei Herkünfte in einem Dialog, deshalb der Schalter `mitBetriebsangaben`:
 *
 * - Die **Vermessung** – Drehzahl, acht Oktavbänder, Gesamtschallleistungspegel – stammt vom
 *   Anlagentyp und wird dort gepflegt (16.1.10).
 * - Die **Betriebsangaben** – ob der Modus gefahren wird und mit welchen Einsatzzeiten – folgen
 *   aus der Genehmigung und stehen nur an der Anlage im Projekt (4.5). Der Hersteller kann sie
 *   nicht kennen; bei schallreduziertem Nachtbetrieb teilt sich die Einsatzzeit auf zwei Modi auf.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import DialogTemplate from "../../../layout/DialogTemplate";
import SubSection, { subSectionFullWidth } from "../../../layout/SubSection";
import Button from "../../../common/inputs/Button";
import InputField from "../../../common/inputs/InputField";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import Dropdown from "../../../common/inputs/Dropdown";
import {
  BetriebsmodusCreate,
  BetriebsmodusFelder,
} from "../../../../services/api/anlagentypen";
import { WertelistenOptions } from "../hooks/useWertelisten";

/**
 * Die Angaben aus 4.5, die es nur an der Anlage gibt.
 *
 * Kein Kennzeichen „wird gefahren": ELiA legt in 4.5 für jeden gefahrenen Betriebszustand eine
 * Zeile an – ein nicht gefahrener Modus hat dort keine. Wer ihn nicht fährt, löscht ihn.
 */
export interface BetriebsangabenWerte {
  einsatzzeit_tage?: number | null;
  einsatzzeit_bezug_id?: string | null;
  einsatzzeit_stunden_pro_tag?: number | null;
  einsatzzeit_zeitfenster?: string | null;
}

type Werte = Partial<BetriebsmodusFelder & BetriebsangabenWerte>;

const LEER: Werte = { bezeichnung: "" };

/** Die acht Oktavbänder, die 16.1.10 je Modus verlangt */
const OKTAVBAENDER = [63, 125, 250, 500, 1000, 2000, 4000, 8000] as const;

interface BetriebsmodusDialogProps {
  open: boolean;
  modus: Werte | null;
  /** Am Anlagentyp aus, an der Anlage an */
  mitBetriebsangaben?: boolean;
  /** Am Anlagentyp bearbeitbar, an der Anlage nur lesbar – die Pegel stammen aus der Vermessung */
  vermessungReadOnly?: boolean;
  wertelisten: WertelistenOptions;
  onClose: () => void;
  onSubmit: (
    werte: BetriebsmodusCreate & BetriebsangabenWerte,
  ) => Promise<void>;
}

const Raster: React.FC<{ children: React.ReactNode; spalten?: number }> = ({
  children,
  spalten = 2,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: `repeat(${spalten}, 1fr)` },
      gap: 2,
      mt: 1,
    }}
  >
    {children}
  </Box>
);

const BetriebsmodusDialog: React.FC<BetriebsmodusDialogProps> = ({
  open,
  modus,
  mitBetriebsangaben = false,
  vermessungReadOnly = false,
  wertelisten,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<Werte>(LEER);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    if (open) {
      setForm(modus ? { ...modus } : LEER);
      setFehler("");
    }
  }, [open, modus]);

  const setzen = useCallback(
    (feld: string, wert: string | number | boolean | null) =>
      setForm((vorher) => ({ ...vorher, [feld]: wert })),
    [],
  );

  const zahl = (feld: keyof Werte) => {
    const wert = form[feld];
    return wert === null || wert === undefined ? "" : String(wert);
  };

  const speichern = async () => {
    if (!form.bezeichnung?.trim()) {
      setFehler(
        "Der Modus wird in 4.5 und 16.1.10 über seine Bezeichnung geführt.",
      );
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ ...form, bezeichnung: form.bezeichnung.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={modus ? "Betriebsmodus bearbeiten" : "Betriebsmodus anlegen"}
      maxWidth="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={speichern} disabled={saving}>
            Speichern
          </Button>
        </>
      }
    >
      {/*
        Reihenfolge: erst der Name, dann wann der Modus gilt, dann was er akustisch bewirkt.
        Die Überschriften sind ELiAs eigene Wörter – „Einsatzzeit" heißen dort alle drei
        Zeitfelder in 4.5, „Schallemissionen" steht im Titel desselben Formulars.
      */}
      <Box sx={{ mt: 1, mb: 4 }}>
        <InputField
          label="Bezeichnung"
          value={form.bezeichnung ?? ""}
          onChange={(v) => {
            setzen("bezeichnung", v);
            if (fehler) setFehler("");
          }}
          error={fehler || undefined}
          readOnly={vermessungReadOnly}
          required
          fullWidth
          placeholder="z. B. Vollast oder Nacht 95 dB"
          tooltip="ELiA nennt den Modus in 16.1.10 „Betriebsmodus“ und in 4.5 „Betriebszustand“ – gemeint ist dasselbe. Weicht der Normalbetrieb in mehr als 10 % der Einsatzzeit ab, verlangt ELiA für den abweichenden Zustand eine eigene Zeile."
        />
      </Box>

      {mitBetriebsangaben && (
        <SubSection title="Einsatzzeiten" defaultExpanded columns={2}>
          <InputFieldNumber
            label="Einsatzzeit"
            value={zahl("einsatzzeit_tage")}
            onChange={(v) => setzen("einsatzzeit_tage", v)}
            suffix="Tage"
            variant="floating"
            tooltip="ELiA: „Geben Sie an, wie oft, wie lange und zu welchen Uhrzeiten der jeweilige Betriebszustand zutrifft.“ Im ausgelegten Antrag steht hier 365 Tage/Jahr."
          />
          <Dropdown
            label="je"
            value={form.einsatzzeit_bezug_id ?? ""}
            options={wertelisten.einsatzzeit_bezug}
            onChange={(v) => setzen("einsatzzeit_bezug_id", v || null)}
            variant="floating"
          />
          <InputFieldNumber
            label="Stunden pro Tag"
            value={zahl("einsatzzeit_stunden_pro_tag")}
            onChange={(v) => setzen("einsatzzeit_stunden_pro_tag", v)}
            suffix="Std."
            variant="floating"
          />
          <InputField
            label="Uhrzeit"
            value={form.einsatzzeit_zeitfenster ?? ""}
            onChange={(v) => setzen("einsatzzeit_zeitfenster", v)}
            fullWidth
            placeholder="z. B. 22:00 – 06:00"
            tooltip="Im ausgelegten Antrag leer, weil die Anlagen durchlaufen. Erst ein zeitlich begrenzter Modus füllt das Feld."
          />
        </SubSection>
      )}

      <SubSection title="Schallemissionen" defaultExpanded columns={2}>
        <InputFieldNumber
          label="Rotorumdrehung"
          value={zahl("rotordrehzahl_pro_min")}
          onChange={(v) => setzen("rotordrehzahl_pro_min", v)}
          readOnly={vermessungReadOnly}
          suffix="1/min"
          variant="floating"
        />
        <InputFieldNumber
          label="Gesamtschallleistungspegel"
          value={zahl("schallleistungspegel")}
          onChange={(v) => setzen("schallleistungspegel", v)}
          readOnly={vermessungReadOnly}
          suffix="dB(A)"
          variant="floating"
        />
        <Box sx={{ ...subSectionFullWidth }}>
          <Raster spalten={4}>
            {OKTAVBAENDER.map((hz) => (
              <InputFieldNumber
                key={hz}
                label={`${hz} Hz`}
                value={zahl(`oktavpegel_${hz}` as keyof Werte)}
                onChange={(v) => setzen(`oktavpegel_${hz}`, v)}
                readOnly={vermessungReadOnly}
                suffix="dB(A)"
                variant="floating"
              />
            ))}
          </Raster>
        </Box>
      </SubSection>
    </DialogTemplate>
  );
};

export default BetriebsmodusDialog;
